import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import LoginPanel from "./components/Login";
import Containers from "./components/Containers";
import Cards from "./components/Cards";
import Home from "./components/Home";
import ResetSenha from "./components/ResetSenha";
import { supabase } from "./supabaseClient";
import "./App.css";

const ContainersWrapper = () => {
  const { containerId } = useParams();
  const validContainerId = containerId && /^[0-9a-fA-F-]{36}$/.test(containerId)
    ? containerId : null;
  return <Containers containerIdDaUrl={validContainerId} />;
};

// =============================================
// CardsWrapper - lê entityId da query string
// =============================================
const CardsWrapper = (props) => {
  const location = useLocation();
  const entityId = new URLSearchParams(location.search).get("entityId") ||
                   location.state?.projectId ||
                   location.state?.setorId ||
                   location.state?.workspaceId ||
                   "default";
  return <Cards key={entityId} {...props} />;
};

export default function App() {
  const location = useLocation();
  const isCardsRoute = location.pathname.startsWith("/cards");
  const isContainersRoute = location.pathname.startsWith("/containers");
  // Containers agora tem seu próprio topbar (busca/notificações/perfil),
  // então o Header global fica de fora — igual já acontecia em /cards.
  const hideGlobalHeader = isCardsRoute || isContainersRoute;

  const [session, setSession]               = useState(null);
  const [profile, setProfile]               = useState(null);
  const [showLoginPanel, setShowLoginPanel] = useState(false);
  const [hasOverdueToday, setHasOverdueToday] = useState(false);
  const [glowDismissed, setGlowDismissed]   = useState(false);
  const [loadingSession, setLoadingSession] = useState(true);

  useEffect(() => {
    const fetchSession = async () => {
      setLoadingSession(true);
      const { data } = await supabase.auth.getSession();
      setSession(data?.session || null);
      setLoadingSession(false);
    };
    fetchSession();
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (s) setShowLoginPanel(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!session?.user) { setProfile(null); return; }
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, nome, empresa, funcao, container, avatar_url, telefone")
          .eq("id", session.user.id).single();
        if (error && error.code !== "PGRST116") throw error;
        setProfile(data || null);
      } catch (err) { console.error("Erro ao buscar perfil:", err.message); }
    };
    fetchProfile();
  }, [session]);

  useEffect(() => {
    const check = async () => {
      if (!session?.user?.id) { setHasOverdueToday(false); return; }
      try {
        const ontem = new Date();
        ontem.setDate(ontem.getDate() - 1);
        const ontemStr = ontem.toISOString().split('T')[0];
        let hasOverdue = false;

        const { data: responsaveis, error: e1 } = await supabase
          .from("ata_objetivos_responsaveis_enriquecidos")
          .select("ata_objetivo_id").eq("usuario_id", session.user.id);
        if (e1) throw e1;
        if (responsaveis?.length) {
          const ids = responsaveis.map(r => r.ata_objetivo_id).filter(Boolean);
          if (ids.length) {
            const { data: obj, error: e2 } = await supabase
              .from("ata_objetivos").select("data_entrega, concluido, texto")
              .in("id", ids).eq("data_entrega", ontemStr);
            if (e2) throw e2;
            if (obj?.some(o => !o.concluido && o.texto && !o.texto.startsWith("[EXCLUIDO]")))
              hasOverdue = true;
          }
        }
        if (!hasOverdue) {
          const { data: com, error: e3 } = await supabase
            .from("comentarios").select("id")
            .eq("agendado_por", session.user.id).eq("data_entrega", ontemStr);
          if (!e3 && com?.length) hasOverdue = true;
        }
        setHasOverdueToday(hasOverdue);
      } catch { setHasOverdueToday(false); }
    };
    check();
  }, [session?.user?.id]);

  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape" && showLoginPanel) setShowLoginPanel(false); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [showLoginPanel]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null); setProfile(null);
    setHasOverdueToday(false); setGlowDismissed(false);
  };

  if (loadingSession) return (
    <div style={{ display:"flex", justifyContent:"center", alignItems:"center", height:"100vh", fontSize:18, color:"#666" }}>
      Carregando...
    </div>
  );

  return (
    <div className="App">
      {/* Header global: fora de /cards e /containers (ambos têm topbar próprio) */}
      {!hideGlobalHeader && (
        <Header
          session={session}
          profile={profile}
          onLoginClick={() => setShowLoginPanel(p => !p)}
          onLogout={handleLogout}
          onProfileUpdate={setProfile}
          hasOverdueToday={hasOverdueToday}
          showGlow={session && !glowDismissed}
          onGlowDismiss={() => setGlowDismissed(true)}
        />
      )}

      {showLoginPanel && !session && (
        <div className="login-panel-container show">
          <LoginPanel onLogin={() => setShowLoginPanel(false)} onClose={() => setShowLoginPanel(false)} />
        </div>
      )}

      <main className={hideGlobalHeader ? "app-main--cards" : "app-main"}>
        <Routes>
          <Route path="/" element={!session ? <Home onOpenLogin={() => setShowLoginPanel(p => !p)} /> : <Navigate to="/containers" replace />} />
          <Route path="/containers" element={session ? <ContainersWrapper /> : <Navigate to="/" replace />} />
          <Route path="/containers/:containerId" element={session ? <ContainersWrapper /> : <Navigate to="/" replace />} />
          
          {/* ============================================= */}
          {/* Rota /cards com CardsWrapper */}
          {/* ============================================= */}
          <Route
            path="/cards/:projectName"
            element={session
              ? <CardsWrapper session={session} profile={profile} onLogout={handleLogout} onProfileUpdate={setProfile} />
              : <Navigate to="/" replace />}
          />
          
          <Route path="/ResetSenha" element={<ResetSenha />} />
          <Route path="*" element={<Navigate to={session ? "/containers" : "/"} replace />} />
        </Routes>
      </main>

      {/* Footer: fora de /cards e /containers */}
      {!hideGlobalHeader && <Footer />}
    </div>
  );
}